import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import GalleryLightbox from '../components/GalleryLightbox';
import SEO from '../components/SEO';

export default function GalleryPage() {
  const { lang, getLoc } = useLanguage();
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/gallery_items');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setItems(data);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // Fallback
      }

      // Hardcoded gallery fallback list
      setItems([
        {
          id: '1',
          category: 'klinikken',
          title: { da: 'Roligt behandlingsrum i Salig', en: 'Serene treatment room at Salig' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/hero-treatment.jpg']
        },
        {
          id: '2',
          category: 'behandlinger',
          title: { da: 'Fokuseret bindevævsmassage', en: 'Focused deep tissue massage' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/deep-tissue.jpg']
        },
        {
          id: '3',
          category: 'behandlinger',
          title: { da: 'Biodynamisk Kranio-Sakral Terapi', en: 'Bio Dynamic Craniosacral Therapy' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/craniosacral.jpg']
        },
        {
          id: '4',
          category: 'behandlinger',
          title: { da: 'Tryg og omsorgsfuld gravidmassage', en: 'Nurturing pregnancy massage' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/pregnancy-massage.jpg']
        },
        {
          id: '5',
          category: 'stemning',
          title: { da: 'Ventelounge med naturligt lys', en: 'Lounge area with natural light' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/clinic-interior.jpg']
        },
        {
          id: '6',
          category: 'stemning',
          title: { da: 'Økologiske massageolier og rene tekstiler', en: 'Organic massage oils and crisp linens' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/gallery-detail-1.jpg']
        },
        {
          id: '7',
          category: 'behandlinger',
          title: { da: 'Nærværende kropsbehandling hos Jette', en: 'Attentive body treatment with Jette' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09361_2.jpg']
        },
        {
          id: '8',
          category: 'behandlinger',
          title: { da: 'Forløsende afspændingsgrebe', en: 'Relieving relaxation techniques' },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09348_2.jpg']
        }
      ]);
      setLoading(false);
    }

    fetchGallery();
  }, []);

  const filteredItems = items.filter((item) => {
    if (activeCategory === 'all') return true;
    return item.category === activeCategory;
  });

  const formattedForLightbox = filteredItems.map((item) => ({
    title: getLoc(item.title),
    image: Array.isArray(item.image) && item.image[0] ? item.image[0] : item.image
  }));

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Billedgalleri' : 'Photo Gallery'}
        description={lang === 'da'
          ? 'Gå på opdagelse i billedgalleriet for Massage with Jette: Salig-behandlingsrummet på Østerbro, stemning og professionel massage i praksis.'
          : 'Explore the photo gallery for Massage with Jette: Salig treatment room in Østerbro, atmosphere, and professional massage therapy.'
        }
      />
      
      {/* Header */}
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
            {lang === 'da' ? 'Billedgalleri' : 'Photo Gallery'}
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {lang === 'da' ? 'Stemning fra behandlingsrummet' : 'Atmosphere & Treatment Space'}
          </h1>
          <p className="text-base sm:text-lg text-[#6E6760] leading-relaxed">
            {lang === 'da'
              ? 'Gå på opdagelse i billederne fra klinikken i Salig på Masnedøgade, behandlingsrummet og arbejdet med massage.'
              : 'Explore photos from the Salig clinic space, treatment room, and massage therapy in action.'}
          </p>
        </div>
      </section>

      {/* Gallery Grid & Filter */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filters */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
            {[
              { id: 'all', label: lang === 'da' ? 'Alle billeder' : 'All Photos' },
              { id: 'behandlinger', label: lang === 'da' ? 'Behandlinger' : 'Treatments' },
              { id: 'klinikken', label: lang === 'da' ? 'Klinikken' : 'Clinic' },
              { id: 'stemning', label: lang === 'da' ? 'Stemning' : 'Atmosphere' }
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-[#2E3191] text-white shadow-xs'
                    : 'bg-[#FAF7F2] text-[#2C2825] hover:bg-[#F4EFEA] border border-[#E8E0D7]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-12 text-center text-[#6E6760]">
              <i className="fa-solid fa-spinner animate-spin text-3xl text-[#2E3191]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredItems.map((item, index) => {
                const imgUrl = Array.isArray(item.image) && item.image[0] ? item.image[0] : item.image;

                return (
                  <div
                    key={item.id || index}
                    onClick={() => setLightboxIndex(index)}
                    className="group relative aspect-4/3 rounded-2xl overflow-hidden border border-[#E8E0D7] bg-[#FAF7F2] cursor-pointer shadow-xs hover:shadow-md transition-all"
                  >
                    <img
                      src={imgUrl}
                      alt={getLoc(item.title)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <p className="text-white text-xs font-semibold font-serif-display">
                        {getLoc(item.title)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryLightbox
        images={formattedForLightbox}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : formattedForLightbox.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < formattedForLightbox.length - 1 ? prev + 1 : 0))}
      />

    </div>
  );
}
